import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisCrosshair,
  ChartCategoryAxisCrosshairTooltip,
  ChartCategoryAxisItem,
  type ChartCategoryAxisItemProps,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartSeriesItemTooltip,
  type ChartSeriesItemTooltipProps,
  ChartTooltip,
  ChartValueAxis,
  ChartValueAxisItem,
  type ChartValueAxisItemProps,
} from '@progress/kendo-react-charts'
import React from 'react'
import * as R from 'remeda'

import { Box } from '~/components/elements/Box'
import { useTamRatio } from '~/hooks/useTamRatio'
import { type Group, toGroupIndex } from '~/utils'

import { useTabulatedGroups } from '../hooks/useTabulatedGroups'
import { useAtomValueContext } from '../providers/AtomsProvider'
import { type RowData } from '../utils'

const getColor = (group: Group) => {
  const bodyStyle = window.getComputedStyle(window.document.body)

  return bodyStyle.getPropertyValue(`--colorGrouping${toGroupIndex(group) - 1}`)
}

export type IndividualChartContentProps<T extends RowData<string>> = {
  getChartCategoryAxisItemLabel: (value: string) => string
  getName: (rowData: T) => string
  getRowId: (rowData: T) => string
  rowsData: T[]
}

export const IndividualChartContent = React.forwardRef(
  <T extends RowData<string>>(
    {
      getChartCategoryAxisItemLabel,
      getName,
      getRowId,
      rowsData,
    }: IndividualChartContentProps<T>,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const dataDisplayType = useAtomValueContext('dataDisplayType')
    const valueAxisItemLabels = React.useMemo<
      ChartValueAxisItemProps['labels']
    >(
      () => ({
        content: ({ value }) =>
          dataDisplayType === 'percentage'
            ? `${value}%`
            : value.toLocaleString(),
      }),
      [dataDisplayType],
    )

    const valueAxisItemMax = React.useMemo<ChartValueAxisItemProps['max']>(
      () => (dataDisplayType === 'percentage' ? 100 : undefined),
      [dataDisplayType],
    )

    const hiddenRowIds = useAtomValueContext('hiddenRowIds')
    const visibleRowsData = React.useMemo(
      () =>
        R.differenceWith(
          rowsData,
          hiddenRowIds,
          (rowData, hiddenRowId) => getRowId(rowData) === hiddenRowId,
        ),
      [getRowId, hiddenRowIds, rowsData],
    )
    const categoryAxisItemCategories = React.useMemo<
      ChartCategoryAxisItemProps['categories']
    >(() => visibleRowsData.map(getName), [getName, visibleRowsData])

    const chartCategoryAxisItemLabels = React.useMemo<
      ChartCategoryAxisItemProps['labels']
    >(
      () => ({
        content: ({ value }) => getChartCategoryAxisItemLabel(value),
        rotation: 90,
      }),
      [getChartCategoryAxisItemLabel],
    )

    const tabulatedGroups = useTabulatedGroups()

    const tamRatio = useTamRatio()

    const groupNameMap = useAtomValueContext('groupNameMap')

    const seriesItemTooltipRender = React.useMemo<
      ChartSeriesItemTooltipProps['render']
    >(
      () =>
        ({ point }) => {
          const value = Number(point.value)
          const valueText =
            dataDisplayType === 'percentage'
              ? `${value.toFixed(1)}%`
              : `${value.toLocaleString()}`

          return `${getName(
            visibleRowsData[point.categoryIndex],
          )}: ${valueText}`
        },
      [getName, dataDisplayType, visibleRowsData],
    )

    return (
      <Box ref={ref}>
        <Chart transitions={false}>
          <ChartLegend orientation="horizontal" position="top" />

          <ChartTooltip />

          <ChartValueAxis>
            <ChartValueAxisItem
              labels={valueAxisItemLabels}
              max={valueAxisItemMax}
              min={0}
            />
          </ChartValueAxis>

          <ChartCategoryAxis>
            <ChartCategoryAxisItem
              categories={categoryAxisItemCategories}
              labels={chartCategoryAxisItemLabels}
            >
              <ChartCategoryAxisCrosshair dashType="dot" width={1}>
                <ChartCategoryAxisCrosshairTooltip />
              </ChartCategoryAxisCrosshair>
            </ChartCategoryAxisItem>
          </ChartCategoryAxis>

          <ChartSeries>
            {tabulatedGroups.map((group) => {
              const data = visibleRowsData.map((rowData) => {
                const ratio = rowData.ratios[group]
                const count = rowData.counts[group]

                return {
                  percentage: ratio * 100,
                  number: count,
                  estimatedPopulation: count * tamRatio,
                }[dataDisplayType]
              })

              return (
                <ChartSeriesItem
                  key={group}
                  color={getColor(group)}
                  data={data}
                  name={groupNameMap[group]}
                >
                  <ChartSeriesItemTooltip render={seriesItemTooltipRender} />
                </ChartSeriesItem>
              )
            })}
          </ChartSeries>
        </Chart>
      </Box>
    )
  },
) as <T extends RowData<string>>(
  props: IndividualChartContentProps<T> & React.RefAttributes<HTMLDivElement>,
) => React.ReactNode
